// /en/apec/$province — EN APEC by-province landing with LocalBusiness + Near-me schema
import { createFileRoute, notFound } from "@tanstack/react-router";
import { ServiceLanding, buildLandingSchemas } from "@/components/ServiceLanding";
import { APEC_PROVINCES, APEC_PROVINCE_SLUGS } from "@/data/apec-provinces";
import { canonicalAndHreflang, ctaDesc, ctaTitle } from "@/lib/seo";

const SITE = "https://nyclanguageinstitute.com";

export const Route = createFileRoute("/en/apec/$province")({
  beforeLoad: ({ params }) => {
    if (!APEC_PROVINCE_SLUGS.includes(params.province)) throw notFound();
  },
  head: ({ params }) => {
    const prov = APEC_PROVINCES.find(p => p.slug === params.province)!;
    const path = `/en/apec/${prov.slug}`;
    const thPath = `/apec/${prov.slug}`;
    const title = `APEC Business Travel Card (ABTC) for ${prov.name} Executives | NYC`;
    const desc = `Apply for the APEC Business Travel Card from ${prov.name}, Thailand. Visa-free business travel across 21 APEC economies. NYC handles MFA filing, pre-clearance and delivery — full English-language support. Send your documents for a free check.`;
    const steps = [
      { name: "Free eligibility check", text: `Confirm director/shareholder/executive status under Thai Nationality, tailored to ${prov.name}-registered companies.` },
      { name: "Document collection", text: `Courier or in-person pickup from your office in ${prov.name}. We prepare APEC-1 form, passport copies, photos, company affidavit and business justification letter.` },
      { name: "MFA Bangkok filing", text: `Our team lodges the ABTC application at the Department of Consular Affairs (MFA Chaeng Wattana) — no Bangkok trip required from ${prov.name}.` },
      { name: "Pre-clearance tracking", text: `21 APEC economies process the application in parallel (3–4 months). We monitor each clearance and report monthly.` },
      { name: "ABTC delivery", text: `Once issued, the 5-year card is sent by EMS to your ${prov.name} address, with an English usage guide for APEC Lane immigration.` },
    ];
    const faqs = [
      { q: `Can I apply for the APEC Card without travelling to Bangkok from ${prov.name}?`, a: `Yes. NYC collects documents at your ${prov.name} office and files at MFA on your behalf. You only need to provide passport, photos and signed forms.` },
      { q: `Is the APEC Card useful for ${prov.name}-based executives?`, a: `${prov.localNote} Primary corridors used by ${prov.name} ABTC holders include ${prov.apecCorridor}.` },
      { q: "Do I need a separate business visa once I hold the ABTC?", a: "No — the ABTC functions as a pre-cleared multi-entry business visa for 19 of the 21 APEC economies (Japan, Korea, China, Australia, NZ, all ASEAN, Mexico, Peru, Chile, PNG, Russia), with stays of 60–90 days per entry. The USA and Canada remain transitional members and grant APEC Lane priority at immigration only, not a pre-cleared visa." },
      { q: "What is the total turnaround from filing?", a: "Approximately 4–6 months from MFA Department of Consular Affairs filing, including parallel pre-clearance from each member economy. The issued ABTC is valid for 5 years with unlimited multi-entry, and NYC tracks every economy's clearance status weekly via LINE @nycli — replacing dozens of separate embassy applications with a single coordinated file." },
      { q: "Are NYC fees the same outside Bangkok?", a: `Yes — flat THB 12,000 service fee for ${prov.name} clients, plus THB 5,000 MFA government fee. Courier costs are included.` },
    ];
    const breadcrumbs = [
      { name: "Home (EN)", path: "/en" },
      { name: "APEC Card", path: "/en/apec" },
      { name: prov.name, path },
    ];
    const localBusiness = {
      type: "application/ld+json" as const,
      children: JSON.stringify({
        "@context": "https://schema.org", "@type": "LegalService", "@id": `${SITE}${path}#local`,
        name: `NYC Legal & Notary Service — APEC Card filing for ${prov.name}`,
        url: `${SITE}${path}`, telephone: "+66922429293", priceRange: "฿฿",
        areaServed: { "@type": "AdministrativeArea", name: prov.nameTh },
        address: { "@type": "PostalAddress", addressCountry: "TH", addressRegion: prov.region, addressLocality: prov.name },
        geo: { "@type": "GeoCoordinates", latitude: prov.lat, longitude: prov.lng },
        geoRadius: "150000",
        knowsLanguage: ["en", "th"],
        makesOffer: { "@type": "Offer", name: "APEC Business Travel Card", priceCurrency: "THB", price: "12000" },
      }),
    };
    return {
      meta: [
        { title: ctaTitle(title, "/en/apec/$province", "en") }, { name: "description", content: ctaDesc(desc, "/en/apec/$province", "en")},
        { property: "og:title", content: ctaTitle(title, "/en/apec/$province", "en")}, { property: "og:description", content: ctaDesc(desc, "/en/apec/$province", "en")},
        { property: "og:url", content: `${SITE}${path}` },
      ],
      links: [
        { rel: "canonical", href: `${SITE}${path}` },
        { rel: "alternate", hrefLang: "th-TH", href: `${SITE}${thPath}` },
        { rel: "alternate", hrefLang: "en", href: `${SITE}${path}` },
        { rel: "alternate", hrefLang: "x-default", href: `${SITE}${thPath}` },
      ],
      scripts: [
        ...buildLandingSchemas({ path, title, description: desc, serviceType: "APEC Business Travel Card Service", priceFromThb: 12000, steps, faqs, breadcrumbs }),
        localBusiness,
      ],
    };
  },
  component: ApecProvinceEn,
});

function ApecProvinceEn() {
  const { province } = Route.useParams();
  const prov = APEC_PROVINCES.find(p => p.slug === province)!;
  const desc = `Apply for the APEC Business Travel Card from ${prov.name}. NYC handles MFA filing, 21-economy pre-clearance and card delivery — no Bangkok trip required. Send your documents for a free check.`;
  return (
    <ServiceLanding
      locale="en"
      badge={`APEC Card · ${prov.name}`}
      title={`APEC Business Travel Card for ${prov.name} Executives`}
      subtitle={`${prov.localNote} Primary APEC corridors used here: ${prov.apecCorridor}. Nearest international gateway: ${prov.nearbyAirport}.`}
      heroBullets={[
        `Visa-free business travel across 21 APEC economies from ${prov.name}`,
        "5-year card validity · unlimited trips · APEC Lane priority immigration",
        `Document pickup in ${prov.name} — we file at MFA Bangkok on your behalf`,
        `Tailored to ${prov.industryFocus}`,
      ]}
      whoFor={{
        th: [`ผู้บริหารบริษัทใน ${prov.nameTh} ที่ต้องเดินทางในภูมิภาค APEC บ่อย`, "นักธุรกิจส่งออก-นำเข้าในเอเชียแปซิฟิก"],
        foreign: [`Foreign directors of ${prov.name}-registered Thai companies eligible under Thai-nationality rules`, "Dual-nationals using a Thai passport for APEC business travel"],
      }}
      steps={[
        { name: "Free eligibility check", text: `Confirm director/shareholder/executive status under Thai Nationality, tailored to ${prov.name}-registered companies.` },
        { name: "Document collection", text: `Courier or in-person pickup from your office in ${prov.name}.` },
        { name: "MFA Bangkok filing", text: `Our team lodges the ABTC at MFA Chaeng Wattana — no Bangkok trip required.` },
        { name: "Pre-clearance tracking", text: `21 APEC economies process in parallel (3–4 months). Monthly status reports.` },
        { name: "ABTC delivery", text: `5-year card delivered by EMS to your ${prov.name} address.` },
      ]}
      pricing={[
        { label: "MFA government fee", price: "5,000", note: "5-year validity" },
        { label: "NYC service fee — Standard", price: "12,000", note: `Includes courier from ${prov.name}` },
        { label: "Premium (rush + concierge)", price: "18,000", note: "Dedicated coordinator" },
      ]}
      faqs={[
        { q: `Can I apply for the APEC Card without travelling to Bangkok from ${prov.name}?`, a: `Yes. NYC collects documents at your ${prov.name} office and files at MFA on your behalf.` },
        { q: `Is the APEC Card useful for ${prov.name}-based executives?`, a: `${prov.localNote} Primary corridors: ${prov.apecCorridor}.` },
        { q: "Do I need a separate business visa once I hold the ABTC?", a: "No. The ABTC is recognised by 19 of the 21 APEC economies as a pre-cleared business visa, valid for 60–90 days per entry depending on destination. USA and Canada participate as transitional members granting APEC Lane fast-track immigration only; a separate B1/B2 or business visitor visa is still required for those two countries." },
        { q: "What is the total turnaround?", a: "Typically 4–6 months from MFA filing, including parallel pre-clearance by every participating economy. The card is then valid for 5 years with unlimited multi-entry. NYC monitors each economy's status weekly and pushes updates via LINE @nycli; renewals are filed 3–6 months before expiry to ensure no travel gap." },
        { q: "Are NYC fees the same outside Bangkok?", a: `Yes — flat THB 12,000 service fee for ${prov.name}, plus THB 5,000 government fee. Courier included.` },
      ]}
      contactNote={desc}
      relatedLinks={[
        { to: "/en/apec", label: "APEC Card — Overview (EN)" },
        { to: "/apec", label: "บริการ APEC Card (TH)" },
        { to: `/locations/${prov.slug}`, label: `NYC ${prov.name} — Branch Info` },
        { to: "/en/visa/non-b", label: "Non-B Business Visa (EN)" },
        { to: "/en/visa/ltr", label: "LTR Visa (EN)" },
        { to: "/en/translate/mfa", label: "MFA Legalization (EN)" },
      ]}
    />
  );
}
